import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import More2FillIcon from "remixicon-react/More2FillIcon";
import { Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "$utils/useSnackbar";
import { Button, Layout, Modal, Text, Tunel } from "$components";
import ActionButtonBar from "./components/ActionButtonBar";
import DeleteModal from "./components/DeleteModal";
import { useEDBFormContext } from "./context/EDBFormContext";
import DeliveryPicker from "./steps/DeliveryPicker";
import MaterialNeed from "./steps/MaterialNeed";
import ProjectDock from "./steps/ProjectDockPicker";
import { useStyles } from "./styles/EDBForm.styles";
import { edbStepLabels } from "./types/edb.types";
import useAuth from "$hooks/useAuth";
import { EdbStatus } from "$types";
import { EDBService } from "$services";
import { snackbarSuccessOptions } from "$utils/SnackbarOptions";

export interface IOrderFormProps {
  className?: string;
}

function EDBFormInside() {
  const {
    idEdb,
    isLoadingIntegratedOrders,
    showCart,
    setShowCart,
    step,
    maxStepIndex,
    changeStep,
    setOpenDeleteEDBModal,
    currentCompany,
    currentEdb,
  } = useEDBFormContext();

  const navigate = useNavigate();
  const { isAdmin, token } = useAuth();
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);

  const { watch } = useFormContext();
  const classes = useStyles();

  // Watch company value
  const companyValue = watch("company");

  // Step content should be reachable even without project/dockyard selected in step 1.
  // Some actions inside steps 2/3 might still require a company, but we don't block navigation.
  const [canRenderSteps23] = useState(true);

  // Get the best company ID to use
  const getEffectiveCompanyId = () => {
    // Always prefer form value first, then context value
    return companyValue || currentCompany || null;
  };

  const showActionsMenu = useMemo(
    () =>
      Boolean(idEdb) &&
      isAdmin() &&
      currentEdb?.status === EdbStatus.CART_CREATED,
    [currentEdb?.status, idEdb, isAdmin]
  );

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleCloseMenu = () => setMenuAnchor(null);

  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);

  return (
    <Layout
      title="Création d'une expression de besoin"
      className={classes.layout}
    >
      <form
        onSubmit={(event) => event.preventDefault()} // We are doing the submit ourselves, but there is another form in this form so we need to prevent this form to submit
        className={classes.form}
      >
        <div style={{ display: "flex", position: "relative" }}>
          <Tunel
            maxStepIndex={maxStepIndex}
            currentStep={step}
            steps={edbStepLabels}
            setStep={changeStep}
          />
          {showActionsMenu && (
            <div className={classes.deleteBtn}>
              <Button onClick={handleOpenMenu} aria-label="Actions EDB">
                <More2FillIcon />
              </Button>
              <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    setOpenDuplicateModal(true);
                  }}
                >
                  Dupliquer l&apos;EDB
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    setOpenDeleteEDBModal(true);
                  }}
                >
                  Supprimer l&apos;EDB
                </MenuItem>
              </Menu>
            </div>
          )}
        </div>
        {/* STEP 1 */}
        <div className={`${classes.step} ${step === 0 && "show"}`}>
          <ProjectDock />
        </div>
        {/* STEP 2 */}
        <div className={`${classes.step} ${step === 1 && "show"}`}>
          {canRenderSteps23 && (
            <MaterialNeed
              companyId={getEffectiveCompanyId()}
              showCart={showCart}
              setShowCart={setShowCart}
              isAddingMaterial={false}
              isLoading={isLoadingIntegratedOrders}
            />
          )}
        </div>
        {/* STEP 3 */}
        <div className={`${classes.step} ${step === 2 && "show"}`}>
          {canRenderSteps23 && (
            <DeliveryPicker edbCompanyId={getEffectiveCompanyId()} />
          )}
        </div>
      </form>
      <ActionButtonBar />
      <DeleteModal idEdb={idEdb} />
      <Modal
        title="Dupliquer l'EDB"
        textValidate="Valider"
        textCancel="Annuler"
        open={openDuplicateModal}
        onValidate={async () => {
          setOpenDuplicateModal(false);
          const duplicated = await EDBService.DuplicateEDB(token, idEdb);
          const newId = duplicated?._id ?? duplicated?.id;
          const newEdbNumber = duplicated?.edbId;
          if (newId) {
            navigate(`/create-edb/${newId}`);
          }
          if (newEdbNumber) {
            openSnackbar(`EDB n° ${newEdbNumber} créée avec succès`);
          }
        }}
        onCancel={() => setOpenDuplicateModal(false)}
      >
        <div className={classes.dialogContent}>
          <Text style={{ whiteSpace: "pre-line" }}>
            {`Vous allez créer une nouvelle EDB en statut Brouillon à partir de cette EDB.
L'étape 1 sera vide et devra être complétée.
Souhaitez-vous continuer ?`}
          </Text>
        </div>
      </Modal>
    </Layout>
  );
}

export default EDBFormInside;
